import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Stack,
  useTheme,
  Box,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { IApprovalStage } from "src/services/thumbz-base-api";
import { Reorder } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useThumbzApi } from "src/services/thumbz-api";
import { queryKeys } from "src/config/query-key";
import toast from "react-hot-toast";
import { getServerErrorArrayMessage } from "src/utils/get-server-error-message";

interface ApprovalReorderFormProps {
  open: boolean;
  stages: IApprovalStage[];
  apv_id: string;
  onClose: () => void;
  onSuccess: (stages: IApprovalStage[]) => void;
}

const reorder = (
  list: IApprovalStage[],
  startIndex: number,
  endIndex: number,
): IApprovalStage[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result.map((item, index) => ({
    ...item,
    stg_order: index + 1,
  }));
};

const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  padding: 1 * 2,
  margin: `0 0 ${4}px 0`,

  // change background colour if dragging
  // background: isDragging ? 'lightgreen' : 'grey',

  // styles we need to apply on draggables
  ...draggableStyle,
});

export const ApprovalReorderForm: React.FC<ApprovalReorderFormProps> = ({
  open,
  stages,
  apv_id,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const { api } = useThumbzApi();
  const { palette } = useTheme();
  const [localStages, setLocalStages] = useState<IApprovalStage[]>([]);

  useEffect(() => {
    const sortedStages = [...stages].sort((a, b) => a.stg_order - b.stg_order);
    setLocalStages(sortedStages);
  }, [stages]);

  const upsertMutation = useMutation({
    mutationFn: api.approvalStage.approvalStageControllerUpsert,
  });

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const reorderedStages = reorder(localStages, result.source.index, result.destination.index);

    setLocalStages(reorderedStages);
  };

  const handleSave = () => {
    const promiseArray = localStages.map((stage) =>
      upsertMutation.mutateAsync({
        apv_id,
        stg_name: stage.stg_name,
        stg_order: stage.stg_order,
        stg_comments: stage.stg_comments ?? "",
        stg_id: stage.stg_id,
      }),
    );

    Promise.all(promiseArray)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.approval.approvalControllerGet(apv_id).queryKey,
        });

        if (onSuccess) {
          onSuccess(localStages);
        }
        toast.success("Etapas reordenadas com sucesso");
        onClose();
      })
      .catch((error) => {
        getServerErrorArrayMessage(error).forEach((message) => toast.error(message));
      });
  };

  const highlightColor = palette.primary.main;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Reordenar Etapas</DialogTitle>
      <DialogContent>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                // style={getListStyle(snapshot.isDraggingOver)}
              >
                {localStages.map((item, index) => (
                  <Draggable key={item.stg_id} draggableId={item.stg_id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                      >
                        <Card
                          variant="outlined"
                          style={{
                            borderRadius: 0,
                            backgroundColor: snapshot.isDragging ? highlightColor : "inherit",
                          }}
                        >
                          <CardContent style={{ cursor: "grab", padding: 6 }}>
                            <Stack direction="row" spacing={4}>
                              <Reorder />
                              {item.stg_name}
                            </Stack>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancelar
        </Button>
        <Button onClick={handleSave} color="primary">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
